import React, { useState } from 'react'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Table from 'components/Table'
import HehmInvoiceReport from 'types/HehmInvoiceReport'
import Api from 'services/api'
import InvoiceTableRow, { PaymentOverride } from './InvoiceTableRow'
import { color } from 'styles/colors'

import { useAlertDialog, useConfirmDialog } from 'hooks/useDialog'

type Props = {
  hehmInvoiceReport: HehmInvoiceReport[]
  refreshCallback: () => Promise<void>
}

type PaymentOverrides = {
  [invoiceNo: string]: PaymentOverride
}

const HehmOracle = ({ hehmInvoiceReport, refreshCallback }: Props) => {
  const { alert } = useAlertDialog()
  const { confirm } = useConfirmDialog()
  const [overrides, setOverrides] = useState<PaymentOverrides>({})
  const [loadingInvoice, setLoadingInvoice] = useState<string | null>(null)
  const [isPayingAll, setIsPayingAll] = useState(false)

  const getOverride = (invoiceNo: string) => overrides[invoiceNo] || { amount: '', refId: '' }

  const setOverrideField = (invoiceNo: string, field: 'amount' | 'refId', value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [invoiceNo]: {
        ...getOverride(invoiceNo),
        [field]: value
      }
    }))
  }

  const handleTriggerExternalPayment = async (invoice: HehmInvoiceReport) => {
    const { dealerId, totalAmount, invoiceNo } = invoice
    const overrideKey = `${dealerId}_${invoiceNo}`
    const override = getOverride(overrideKey)
    const paymentAmount = override.amount || totalAmount.toString()
    const paymentRefId = override.refId || invoiceNo
    const isConfirmed = await confirm({
      title: 'Confirm External Payment',
      description: `Are you sure you want to trigger an external payment for Dealer ID ${dealerId} with amount $${paymentAmount} and reference ${paymentRefId}?`,
      confirmText: 'Yes, Trigger Payment'
    })

    if (!isConfirmed) {
      return
    }

    const acctType = 'hehm'
    setLoadingInvoice(dealerId.toString())
    try {
      await Api.Paywall.postExternalPayment(
        dealerId,
        acctType,
        paymentAmount,
        paymentRefId
      )
    } finally {
      await refreshCallback()
      setLoadingInvoice(null)
      setOverrides((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [overrideKey]: _, ...rest } = prev
        return rest
      })
    }
  }

  const handleTriggerAllPayments = async () => {
    const isConfirmed = await confirm({
      title: 'Confirm External Payments',
      description: 'Are you sure you want to trigger payments for all in Aria?',
      confirmText: 'Yes, Trigger Payments'
    })

    if (!isConfirmed) {
      return
    }

    const paymentRefs = hehmInvoiceReport.map((invoice) => {
      const overrideKey = `${invoice.dealerId}_${invoice.invoiceNo}`
      const override = getOverride(overrideKey)
      return override.refId || invoice.invoiceNo
    })

    setIsPayingAll(true)
    const results = await Promise.allSettled(
      hehmInvoiceReport.map((invoice, index) => {
        const { dealerId, totalAmount, invoiceNo } = invoice
        const overrideKey = `${dealerId}_${invoiceNo}`
        const override = getOverride(overrideKey)
        const paymentAmount = override.amount || totalAmount.toString()
        return Api.Paywall.postExternalPayment(dealerId, 'hehm', paymentAmount, paymentRefs[index])
      })
    )

    await refreshCallback()
    setIsPayingAll(false)
    setOverrides({})

    const failedRefs = results
      .map((result, index) => result.status === 'rejected' ? paymentRefs[index] : null)
      .filter(Boolean)

    if (failedRefs.length > 0) {
      alert({
        title: 'Payment Results',
        description: `Completed with ${failedRefs.length} failure(s) out of ${results.length} payments.\n\nFailed: ${failedRefs.join(', ')}`
      })
    } else {
      alert({
        title: 'Payment Results',
        description: 'Triggered Aria payments for all invoices in the report'
      })
    }
  }
  return (
    <S.TableContainer>
      <S.TableInner>
        <Table
          items={hehmInvoiceReport}
          noDataMessage={{
            title: 'No Invoice Data',
            description: 'There are currently no invoices to display.'
          }}
        >
          <S.Header sticky>
            <S.HeaderCell title="Dealer ID" />
            <S.HeaderCell title="Dealer Name" />
            <S.HeaderCell title="Organization" />
            <S.HeaderCell title="Location" />
            <S.HeaderCell title="R12 Account" />
            <S.HeaderCell title="Salesperson" />
            <S.HeaderCell title="Branch" />
            <S.WideHeaderCell title="Charges" />
            <S.WideHeaderCell title="Taxes" />
            <S.HeaderCell title="Total" />
            <S.HeaderCell title="GL Code" />
            <S.HeaderCell title="SKU" />
            <S.HeaderCell title="Amount Paid" />
            <S.HeaderCell title="Reference" />
            <S.HeaderCell title="Action" />
          </S.Header>

          <S.ScrollableContent>
            {hehmInvoiceReport.map((invoice, index) => (
              <InvoiceTableRow
                key={`${invoice.invoiceNo}-${invoice.dealerId}`}
                invoice={invoice}
                isEven={index % 2 === 0}
                override={getOverride(invoice.dealerId + '_' + invoice.invoiceNo)}
                onOverrideChange={(field, value) => setOverrideField(invoice.dealerId + '_' + invoice.invoiceNo, field, value)}
                onPayPress={() => handleTriggerExternalPayment(invoice)}
                showSpinner={loadingInvoice === invoice.dealerId.toString()}
              />
            ))}
          </S.ScrollableContent>
        </Table>
        {hehmInvoiceReport.length > 0 && (
          <S.FooterActions>
            <Button
              decoration="danger"
              text="Pay All in Aria"
              onPress={() => handleTriggerAllPayments()}
              showSpinner={isPayingAll}
            />
          </S.FooterActions>
        )}
      </S.TableInner>
    </S.TableContainer>
  )
}

export default HehmOracle

const S = {
  TableContainer: styled.div`
    background-color: ${color('hehmQuantityBoxBackground')};
    border-radius: 10px;
    box-shadow: 0 2px 8px ${color('shadowSubtle')};
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${color('hehmOracleTableBackground')};
      border-radius: 4px;
    }
  `,
  TableInner: styled.div`
    min-width: max-content;
  `,
  Header: styled(Table.Header)`
    background-color: ${color('tableHeaderBackground')};
    border-radius: 10px 10px 0 0;
    min-width: max-content;
  `,
  HeaderCell: styled(Table.HeaderCell)`
    background-color: ${color('tableHeaderBackground')};
    font-size: 12px;
    padding: 12px 8px;
    min-width: 80px;
    margin-right: 1px;
  `,
  WideHeaderCell: styled(Table.HeaderCell)`
    background-color: ${color('tableHeaderBackground')};
    font-size: 12px;
    padding: 12px 8px;
    min-width: 160px;
    flex: 2;
  `,
  ScrollableContent: styled.div`
    max-height: 600px;
    overflow-y: auto;
    min-width: max-content;

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${color('hehmOracleTableBackground')};
      border-radius: 4px;
    }
  `,
  FooterActions: styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 16px;
    border-top: 1px solid ${color('border')};
    background-color: ${color('tableHeaderBackground')};
  `
}
